import { Link } from "react-router-dom";
import { ArrowLeft, Check, Copy, Loader2, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { inputClass } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { copyText } from "@/lib/format";

export const inputCls = inputClass;
export const selectCls = inputClass;
export const labelCls = "text-sm font-medium text-foreground";
export const helpCls = "text-xs text-muted-foreground";
export const errorCls = "text-sm text-destructive";

// Legacy size names: "md" -> sm; "lg" stays a full-width tap target (tenant pages).
const SIZE = { md: "sm", sm: "sm", xs: "xs", default: "default", lg: "lg", xl: "xl" };
const Btn = (variant) => ({ to, children, testid, type = "button", size = "sm", className, ...rest }) => {
  const cls = cn(size === "lg" && "w-full", className);
  if (to) return <Button asChild variant={variant} size={SIZE[size] || size} className={cls}><Link to={to} data-testid={testid}>{children}</Link></Button>;
  return <Button type={type} variant={variant} size={SIZE[size] || size} className={cls} data-testid={testid} {...rest}>{children}</Button>;
};
export const PrimaryButton = Btn("default");
export const SecondaryButton = Btn("outline");
export const GhostButton = Btn("ghost-muted");
export const DangerButton = Btn("destructive-soft");

export const Page = ({ wide, children }) => (
  <div className={cn("mx-auto flex w-full flex-col gap-8 px-4 py-6 md:px-8 md:py-8", wide ? "max-w-6xl" : "max-w-3xl")}>{children}</div>
);

export const PageHeader = ({ title, description, back, action }) => (
  <div className="flex flex-col gap-3">
    {back && (
      <Link to={back.to} className="-ml-1 inline-flex w-fit items-center gap-1 rounded-md px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground" data-testid="page-back">
        <ArrowLeft className="size-3.5" /> {back.label}
      </Link>
    )}
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold" data-testid="page-title">{title}</h1>
        {description && <div className="text-sm text-muted-foreground">{description}</div>}
      </div>
      {action}
    </div>
  </div>
);

export const SectionTitle = ({ children, className = "" }) => <h2 className={cn("mb-1 text-base font-semibold text-foreground", className)}>{children}</h2>;

export const Notice = ({ children, testid }) => (
  <div className="rounded-lg bg-panel px-3 py-2.5 text-sm text-foreground ring-1 ring-border" data-testid={testid}>{children}</div>
);

export const HairlineRow = ({ children, testid }) => (
  <div className="flex items-center gap-2.5 border-b py-2 text-sm" data-testid={testid}>{children}</div>
);

export const Field = ({ label, help, children }) => (
  <label className="grid content-start gap-2">
    <span className={labelCls}>{label}</span>
    {children}
    {help && <span className={helpCls}>{help}</span>}
  </label>
);

export const Tag = ({ variant = "muted", yellow, children, testid }) => (
  <Badge variant={yellow ? "brand" : variant} data-testid={testid}>{children}</Badge>
);

const PROGRESS = {
  not_started: ["Not started", "bg-muted-foreground/40"],
  in_progress: ["In progress", "bg-warning"],
  documents_complete: ["Complete", "bg-success"],
};
export const ProgressTag = ({ status, testid }) => {
  const [label, dot] = PROGRESS[status] || PROGRESS.not_started;
  return <Badge variant="outline" data-testid={testid}><span className={cn("size-1.5 rounded-full", dot)} />{label}</Badge>;
};

const VERDICT = { pass_documents_only: ["Valid", "success"], does_not_meet: ["Invalid", "destructive"], needs_review: ["In review", "warning"] };
export const VerdictTag = ({ status, outcome, testid }) => {
  if (status !== "documents_complete" || !VERDICT[outcome]) return null;
  const [label, variant] = VERDICT[outcome];
  return <Badge variant={variant} data-testid={testid}>{label}</Badge>;
};

export const FraudTag = ({ testid }) => <Badge variant="destructive" data-testid={testid}><ShieldAlert /> Fraud flag</Badge>;

export const DecisionTag = ({ decision, testid }) => {
  if (decision?.decision === "accepted") return <Badge variant="default" data-testid={testid}><Check /> Accepted</Badge>;
  if (decision?.decision === "declined") return <Badge variant="muted" data-testid={testid}>Declined</Badge>;
  return null;
};

export const ListingTag = ({ status, testid }) =>
  status === "rented" ? <Badge variant="muted" data-testid={testid}>Rented</Badge> : <Badge variant="success" data-testid={testid}>Available</Badge>;

const CIRCLE_LABEL = { passed: "Meets the criterion", failed: "Doesn't meet the criterion", pending: "Not checked yet", warn: "Needs review" };
export const StatusCircle = ({ status }) => (
  <span
    className={cn("grid size-5 shrink-0 place-items-center rounded-full",
      status === "passed" && "bg-success text-white",
      status === "failed" && "bg-destructive text-white",
      status === "warn" && "bg-warning text-white",
      status === "pending" && "border-[1.5px] border-dashed border-muted-foreground/40")}
    title={CIRCLE_LABEL[status]} aria-label={CIRCLE_LABEL[status]} data-status={status}
  >
    {status === "passed" && <Check className="size-3" strokeWidth={3} />}
    {status === "failed" && <X className="size-3" strokeWidth={3} />}
    {status === "warn" && <span className="text-[11px] font-bold leading-none">!</span>}
  </span>
);

export const ProgressBar = ({ value, total, className }) => (
  <div className={cn("h-1.5 overflow-hidden rounded-full bg-muted", className)}>
    <div className="h-full rounded-full bg-brand-strong transition-[width] duration-500" style={{ width: `${(value / Math.max(1, total)) * 100}%` }} />
  </div>
);

export const CopyButton = ({ value, label = "Copied", testid }) => (
  <Button variant="ghost" size="icon-sm" className="text-muted-foreground" aria-label="Copy" data-testid={testid}
    onClick={async (e) => { e.stopPropagation(); (await copyText(value)) ? toast.success(label) : toast.error("Couldn't copy"); }}>
    <Copy />
  </Button>
);

export const CopyRow = ({ label, value, testid }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate font-mono text-xs" data-testid={`${testid}-value`}>{value}</p>
    </div>
    <CopyButton value={value} label={`${label} copied`} testid={`${testid}-copy`} />
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid }) => (
  <div className="flex flex-col items-center rounded-xl border border-dashed px-6 py-14 text-center" data-testid={testid}>
    {Icon && <span className="grid size-9 place-items-center rounded-full bg-muted text-muted-foreground"><Icon className="size-4" /></span>}
    <p className="mt-3 text-sm font-medium">{title}</p>
    {text && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Loading = ({ testid }) => (
  <div className="flex justify-center py-16" data-testid={testid}><Loader2 className="size-4 animate-spin text-muted-foreground" /></div>
);
